<template>
  <div>
    <!-- Desktop table -->
    <el-table
      v-if="!isMobile"
      :data="tableData"
      :row-class-name="getRowClasses"
      @row-click="mergedSettings.handleRowClick"
    >
      <el-table-column
        v-for="column in filteredTableColumnsDesktop"
        :key="column.label"
        :width="column.width || null"
        :min-width="column.minWidth || null"
        :prop="column.prop"
        :label="column.label"
      >
        <template
          v-if="!column.compound"
          #default="{ row }"
        >
          <!-- Optional custom cell: pass a `cell-<prop>` slot -->
          <slot v-if="$slots['cell-' + column.prop]" :name="'cell-' + column.prop" :row="row" />
          <div v-else-if="column.isImage">
            <img class="avatar" :src="row[column.prop]" alt="Image" />
          </div>
          <div
            v-else-if="row[column.prop] !== null && row[column.prop] !== ''"
          >
            {{ row[column.prop] }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        v-if="mergedSettings.showActions"
        :min-width="100"
        align="right"
        :label="$t('common.actions')"
      >
        <template #default="{ $index, row }">
          <el-checkbox
            v-if="mergedSettings.enableSelection && mergedSettings.shouldShowSelection(row)"
            :model-value="mergedSettings.isSelected(row.id)"
            @change="mergedSettings.toggleSelection(row, $event)"
            @click.stop
          />
          <el-button
            v-if="mergedSettings.enableCustomAction && mergedSettings.shouldShowCustomAction(row)"
            type="success"
            circle
            @click.stop="mergedSettings.customAction($index, row)"
          >
            <el-icon>
              <component :is="mergedSettings.customActionIcon || 'House'" />
            </el-icon>
          </el-button>
          <el-button
            v-if="mergedSettings.enableEdit && row.canEdit !== false"
            type="warning"
            circle
            @click.stop="mergedSettings.handleEdit($index, row)"
          >
            <el-icon>
              <Edit />
            </el-icon>
          </el-button>
          <el-button
            v-if="mergedSettings.enableDelete"
            type="danger"
            circle
            @click.stop="mergedSettings.handleDelete($index, row)"
          >
            <el-icon>
              <Delete />
            </el-icon>
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Mobile table -->
    <el-table
      v-else
      :data="tableData"
      :row-class-name="getRowClasses"
      @row-click="mergedSettings.handleRowClick"
    >
      <el-table-column
        v-for="column in filteredTableColumnsMobile"
        :key="column.label"
        :width="column.mobileWidth || null"
        :min-width="column.mobileMinWidth || null"
        :prop="column.prop"
        :label="column.label"
      >
        <template
          v-if="column.isCompound || column.showOnMobile"
          #default="{ row }"
        >
          <slot v-if="$slots['cell-' + column.prop]" :name="'cell-' + column.prop" :row="row" />
          <img v-else-if="column.isImage" class="avatar" :src="row[column.prop]" alt="Image" />

          <div v-else-if="!column.isCompound && !column.isImage">
            {{ row[column.prop] }}
          </div>

          <div v-if="column.isCompound">
            <div
              v-for="(value, key) in column.compound"
              :key="key"
            >
              <strong
                v-if="row[value] !== null && row[value] !== ''"
              >
                {{ $t(key) }}:
              </strong>
              {{ row[value] }}
            </div>
          </div>

          <div
            v-if="mergedSettings.showActions
                   && column.isCompound
                   && !mergedSettings.actionsInline"
            class="action-buttons"
          >
            <el-checkbox
              v-if="mergedSettings.enableSelection && mergedSettings.shouldShowSelection(row)"
              :model-value="mergedSettings.isSelected(row.id)"
              @change="mergedSettings.toggleSelection(row, $event)"
              @click.stop
            />

            <el-button
              v-if="mergedSettings.enableCustomAction && mergedSettings.shouldShowCustomAction(row)"
              type="success"
              circle
              @click.stop="mergedSettings.customAction($index, row)"
            >
              <el-icon>
                <component :is="mergedSettings.customActionIcon || 'House'" />
              </el-icon>
            </el-button>

            <el-button
              v-if="mergedSettings.enableEdit && row.canEdit !== false"
              type="warning"
              circle
              @click.stop="mergedSettings.handleEdit($index, row)"
            >
              <el-icon>
                <Edit />
              </el-icon>
            </el-button>

            <el-button
              v-if="mergedSettings.enableDelete"
              type="danger"
              circle
              @click.stop="mergedSettings.handleDelete($index, row)"
            >
              <el-icon>
                <Delete />
              </el-icon>
            </el-button>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        v-if="mergedSettings.showActions && actionsInline"
        :width="setButtonWidth"
        align="right"
      >
        <template #default="{ $index, row }">
          <div class="side-action-buttons">
            <el-checkbox
              v-if="mergedSettings.enableSelection && mergedSettings.shouldShowSelection(row)"
              :model-value="mergedSettings.isSelected(row.id)"
              @change="mergedSettings.toggleSelection(row, $event)"
              @click.stop
            />

            <el-button
              v-if="mergedSettings.enableCustomAction && mergedSettings.shouldShowCustomAction(row)"
              type="success"
              circle
              @click.stop="mergedSettings.customAction($index, row)"
            >
              <el-icon>
                <component :is="mergedSettings.customActionIcon || 'House'" />
              </el-icon>
            </el-button>

            <el-button
              v-if="mergedSettings.enableEdit && row.canEdit !== false"
              type="warning"
              circle
              @click.stop="mergedSettings.handleEdit($index, row)"
            >
              <el-icon>
                <Edit />
              </el-icon>
            </el-button>

            <el-button
              v-if="mergedSettings.enableDelete"
              type="danger"
              circle
              @click.stop="mergedSettings.handleDelete($index, row)"
            >
              <el-icon>
                <Delete />
              </el-icon>
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { ElTable, ElTableColumn, ElButton, ElCheckbox } from 'element-plus';

export default {
  name: 'ElBaseTable',

  components: {
    ElTable,
    ElTableColumn,
    ElButton,
    ElCheckbox
  },

  props: {
    tableData: {
      type: Array,
      default: () => []
    },
    tableColumns: {
      type: Array,
      default: () => []
    },
    tableSettings: {
      type: Object,
      default: null
    },
    allowClick: {
      type: Boolean,
      default: false
    },
    actionsInline: {
      type: Boolean,
      default: false
    },
    enableEdit: {
      type: Boolean,
      default: false
    },
    enableDelete: {
      type: Boolean,
      default: false
    },
    enableCustomAction: {
      type: Boolean,
      default: false
    },
    customAction: {
      type: Function,
      default: null
    },
    customActionIcon: {
      type: String,
      default: null
    },
    shouldShowCustomAction: {
      type: Function,
      default: null
    },
    enableSelection: {
      type: Boolean,
      default: false
    },
    shouldShowSelection: {
      type: Function,
      default: null
    },
    selectedIds: {
      type: Array,
      default: () => []
    },
    showRedLines: {
      type: Boolean,
      default: false
    },
    redLineProperty: {
      type: String,
      default: ''
    }
  },

  emits: ['row-click', 'edit', 'delete', 'selection-change'],

  data() {
    return {
      isMobile: window.innerWidth <= 1024
    };
  },

  computed: {
    filteredTableColumnsDesktop() {
      return this.tableColumns.filter(col => !col.compound);
    },
    filteredTableColumnsMobile() {
      return this.tableColumns.filter(
        col => col.compound || col.showOnMobile
      );
    },
    mergedSettings() {
      const cfg = {
        ...this.$props,
        ...this.tableSettings,
        handleRowClick: this.tableSettings?.handleRowClick || this.handleRowClick,
        handleEdit: this.tableSettings?.handleEdit || this.handleEdit,
        handleDelete: this.tableSettings?.handleDelete || this.handleDelete,
        customAction: this.tableSettings?.customAction || this.customAction || (() => {}),
        shouldShowCustomAction: this.tableSettings?.shouldShowCustomAction || this.shouldShowCustomAction || (() => true),
        shouldShowSelection: this.tableSettings?.shouldShowSelection || this.shouldShowSelection || (() => true),
        isSelected: this.isSelected,
        toggleSelection: this.toggleSelection,
      };
      cfg.showActions = cfg.enableEdit || cfg.enableDelete || cfg.enableCustomAction || cfg.enableSelection;
      return cfg;
    },
    setButtonWidth() {
      const activeButtons = [this.enableEdit, this.enableDelete, this.enableCustomAction, this.enableSelection].filter(Boolean).length;
      if (activeButtons >= 3) return 140;
      if (activeButtons === 2) return 100;
      return 60;
    }
  },

  mounted() {
    window.addEventListener('resize', this.updateIsMobile);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateIsMobile);
  },

  methods: {
    handleRowClick(index, row) {
      this.$emit('row-click', index, row);
    },
    handleEdit(index, row) {
      this.$emit('edit', index, row);
    },
    handleDelete(index, row) {
      this.$emit('delete', index, row);
    },
    isSelected(id) {
      const ids = this.tableSettings?.selectedIds ?? this.selectedIds;
      return ids.includes(id);
    },
    toggleSelection(row, checked) {
      this.$emit('selection-change', { row, checked });
    },
    updateIsMobile() {
      this.isMobile = window.innerWidth <= 1024;
    },
    getRowClasses({ row }) {
      let classes = '';
      if (this.showRedLines && row[this.redLineProperty]) {
        classes += 'red-row ';
      }
      if (this.allowClick && !this.isMobile) {
        classes += 'clickable-row';
      }
      return classes.trim();
    }
  }
};
</script>

<style>
.clickable-row {
  cursor: pointer;
}
.action-buttons,
.side-action-buttons {
  display: flex;
  justify-content: center;
}
/* .side-action-buttons {
  float: right;
} */
.red-row {
  background-color: rgb(214, 123, 123) !important;
}

/* .el-table table th, .el-table table th.is-leaf {
  background-color: #d9dfed;
} */

</style>
